import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQueryWithReauth';
import type {
  GetOrganizationInvitesResponse,
  GetOrganizationInvitesParams,
  GetPendingInvitesResponse,
  CreateOrganizationInviteParams,
  StoreOrganizationInviteResponse,
  ChangeInviteStatusParams,
  OrganizationJoinRequestResponse,
} from './models';

const ORGANIZATION_INVITES = 'organization_invites';

export const organizationInvitesApi = createApi({
  reducerPath: ORGANIZATION_INVITES,
  baseQuery: baseQueryWithReauth,
  tagTypes: [ORGANIZATION_INVITES],
  endpoints: (build) => ({
    getOrganizationInvites: build.query<
      GetOrganizationInvitesResponse,
      GetOrganizationInvitesParams
    >({
      query: ({ isOrganizer, page }) => {
        const params = new URLSearchParams();
        if (page) {
          params.append('page', page.toString());
        }
        const queryString = params.toString();
        return {
          url: `/${isOrganizer ? 'organizer' : 'partner'}/organization-invites${queryString ? '?' + queryString : ''}`,
          headers: { Accept: 'application/json' },
        };
      },
      providesTags: [{ type: ORGANIZATION_INVITES, id: 'LIST' }],
    }),

    createOrganizationInvite: build.mutation<
      StoreOrganizationInviteResponse,
      CreateOrganizationInviteParams
    >({
      query: ({ isOrganizer, ...body }) => ({
        url: `/${isOrganizer ? 'organizer' : 'partner'}/organization-invites`,
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: ORGANIZATION_INVITES, id: 'LIST' }],
    }),

    getPendingInvites: build.query<GetPendingInvitesResponse, { isOrganizer?: boolean }>({
      query: ({ isOrganizer }) => ({
        url: `/${isOrganizer ? 'organizer' : 'partner'}/organization-invites/pending`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: ORGANIZATION_INVITES, id: 'PENDING' }],
    }),

    changeInviteStatus: build.mutation<StoreOrganizationInviteResponse, ChangeInviteStatusParams>({
      query: ({ isOrganizer, id, status, transition }) => ({
        url: `/${isOrganizer ? 'organizer' : 'partner'}/organization-invites/${id}/status/${status}`,
        method: 'PATCH',
        body: { transition },
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [
        { type: ORGANIZATION_INVITES, id: 'LIST' },
        { type: ORGANIZATION_INVITES, id: 'PENDING' },
      ],
    }),

    getInviteByHash: build.query<StoreOrganizationInviteResponse, string>({
      query: (hash) => ({
        url: `/invites/${hash}`,
        headers: { Accept: 'application/json' },
      }),
    }),

    // a request to join the account from the start page — the page its email opens
    getJoinRequest: build.query<OrganizationJoinRequestResponse, number>({
      query: (id) => ({
        url: `/partner/organization-join-requests/${id}`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, id) => [{ type: ORGANIZATION_INVITES, id: `JOIN_${id}` }],
    }),

    decideJoinRequest: build.mutation<
      OrganizationJoinRequestResponse,
      { id: number; decision: 'approve' | 'decline' }
    >({
      query: ({ id, decision }) => ({
        url: `/partner/organization-join-requests/${id}/${decision}`,
        method: 'POST',
        headers: { Accept: 'application/json' },
      }),
      // an approval adds a member: the team list is read again
      invalidatesTags: (_result, _error, { id }) => [
        { type: ORGANIZATION_INVITES, id: `JOIN_${id}` },
        { type: ORGANIZATION_INVITES, id: 'LIST' },
      ],
    }),

    acceptEventInvite: build.mutation<unknown, { hash: string; onboarding_status?: string }>({
      query: ({ hash, onboarding_status }) => ({
        url: `/invites/event/${hash}/accept`,
        method: 'POST',
        body: onboarding_status ? { onboarding_status } : undefined,
        headers: { Accept: 'application/json' },
      }),
    }),
  }),
});

export const {
  useGetOrganizationInvitesQuery,
  useCreateOrganizationInviteMutation,
  useGetPendingInvitesQuery,
  useChangeInviteStatusMutation,
  useGetInviteByHashQuery,
  useAcceptEventInviteMutation,
  useGetJoinRequestQuery,
  useDecideJoinRequestMutation,
} = organizationInvitesApi;
